/**
 * QuickActionsBar — Persistent action strip at the bottom of EntityDetailPanel
 *
 * Shows context-aware actions based on the entity's current status and primitive type.
 * Always visible (not a tab) — reduces clicks for the most common operations.
 *
 * DUMB FRONTEND: Available actions derived from entity state, not hardcoded per type.
 */

import { useState } from 'react';
import {
  Group,
  Button,
  Text,
  Modal,
  Stack,
  Divider,
  Tooltip,
  Box,
} from '@mantine/core';
import {
  IconCheck,
  IconX,
  IconPlayerPlay,
  IconArchive,
  IconRefresh,
  IconUserPlus,
} from '@tabler/icons-react';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import type { PrimitiveType } from '@/config/primitive-metadata';

// ============================================================================
// ACTION DEFINITIONS
// ============================================================================

interface ActionDef {
  id: string;
  label: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: React.ComponentType<any>;
  color: string;
  /** Status values that enable this action */
  enabledStatuses: string[];
  /** Target status after action (for confirmation dialog) */
  targetStatus?: string;
  /** Requires confirmation dialog */
  requiresConfirmation: boolean;
  /** Confirmation message */
  confirmMessage?: string;
  /** Keyboard shortcut hint */
  shortcut?: string;
}

const COMMON_ACTIONS: ActionDef[] = [
  {
    id: 'activate',
    label: 'Activate',
    icon: IconPlayerPlay,
    color: 'green',
    enabledStatuses: ['DRAFT', 'PENDING', 'PAUSED'],
    targetStatus: 'ACTIVE',
    requiresConfirmation: false,
    shortcut: 'A',
  },
  {
    id: 'complete',
    label: 'Complete',
    icon: IconCheck,
    color: 'green',
    enabledStatuses: ['ACTIVE', 'IN_PROGRESS'],
    targetStatus: 'COMPLETED',
    requiresConfirmation: true,
    confirmMessage: 'Mark this entity as completed? This action may trigger downstream workflows.',
    shortcut: 'C',
  },
  {
    id: 'archive',
    label: 'Archive',
    icon: IconArchive,
    color: 'red',
    enabledStatuses: ['ACTIVE', 'COMPLETED', 'DRAFT', 'PAUSED', 'PENDING'],
    targetStatus: 'ARCHIVED',
    requiresConfirmation: true,
    confirmMessage: 'Archive this entity? It will be soft-deleted and can be restored later.',
    shortcut: 'Del',
  },
  {
    id: 'reopen',
    label: 'Reopen',
    icon: IconRefresh,
    color: 'blue',
    enabledStatuses: ['COMPLETED', 'ARCHIVED'],
    targetStatus: 'ACTIVE',
    requiresConfirmation: true,
    confirmMessage: 'Reopen this entity? It will be set back to Active status.',
  },
];

/** Task-specific actions — status-aware workflow buttons */
const TASK_ACTIONS: ActionDef[] = [
  {
    id: 'start',
    label: 'Start Task',
    icon: IconPlayerPlay,
    color: 'cyan',
    enabledStatuses: ['PENDING'],
    targetStatus: 'IN_PROGRESS',
    requiresConfirmation: false,
    shortcut: 'S',
  },
  {
    id: 'complete',
    label: 'Mark Complete',
    icon: IconCheck,
    color: 'teal',
    enabledStatuses: ['IN_PROGRESS', 'ACTIVE'],
    targetStatus: 'COMPLETED',
    requiresConfirmation: true,
    confirmMessage: 'Mark this task as complete? It will be sent for supervisor approval.',
    shortcut: 'C',
  },
  {
    id: 'approve',
    label: 'Approve',
    icon: IconCheck,
    color: 'green',
    enabledStatuses: ['COMPLETED'],
    targetStatus: 'APPROVED',
    requiresConfirmation: true,
    confirmMessage: 'Approve this completed task? This confirms the work meets requirements.',
    shortcut: 'Y',
  },
  {
    id: 'reject',
    label: 'Reject',
    icon: IconX,
    color: 'red',
    enabledStatuses: ['COMPLETED'],
    targetStatus: 'FAILED',
    requiresConfirmation: true,
    confirmMessage: 'Reject this task? It will be sent back for rework.',
    shortcut: 'N',
  },
  {
    id: 'reassign',
    label: 'Reassign',
    icon: IconUserPlus,
    color: 'blue',
    enabledStatuses: ['PENDING', 'IN_PROGRESS', 'ACTIVE'],
    requiresConfirmation: false,
    shortcut: 'R',
  },
];

/** Primitive types that support task-specific actions (approve/reject/reassign) */
const TASK_ACTION_TYPES: ReadonlySet<string> = new Set(['task', 'approval']);

/** Get available actions for a primitive type and current status */
function getAvailableActions(primitiveType: PrimitiveType, status: string | undefined): ActionDef[] {
  if (!status) return [];

  // Add task-specific actions for types in the metadata set
  if (TASK_ACTION_TYPES.has(primitiveType)) {
    // Tasks use their own workflow: Start → Mark Complete → Approve
    // Exclude generic activate/complete from common actions to avoid duplicates
    const commonWithoutTaskOverlaps = COMMON_ACTIONS.filter(
      a => !['activate', 'complete'].includes(a.id)
    );
    const actions = [...TASK_ACTIONS, ...commonWithoutTaskOverlaps];
    return actions.filter(action => action.enabledStatuses.includes(status));
  }

  // Non-task primitives use common actions
  return COMMON_ACTIONS.filter(action => action.enabledStatuses.includes(status));
}

// ============================================================================
// PROPS
// ============================================================================

interface QuickActionsBarProps {
  entity: PrimitiveItem | null;
  primitiveType: PrimitiveType;
  isReadOnly?: boolean;
  onAction?: (actionId: string, targetStatus?: string) => void;
  /** Additional action buttons rendered after the standard actions */
  extraActions?: React.ReactNode;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function QuickActionsBar({
  entity,
  primitiveType,
  isReadOnly = false,
  onAction,
  extraActions,
}: Readonly<QuickActionsBarProps>) {
  const [confirmAction, setConfirmAction] = useState<ActionDef | null>(null);

  const currentStatus = entity?.status as string | undefined;
  const availableActions = getAvailableActions(primitiveType, currentStatus);

  if (!entity || isReadOnly || (availableActions.length === 0 && !extraActions)) {
    return null;
  }

  const handleAction = (action: ActionDef) => {
    if (action.requiresConfirmation) {
      setConfirmAction(action);
    } else {
      onAction?.(action.id, action.targetStatus);
    }
  };

  const handleConfirm = () => {
    if (confirmAction) {
      onAction?.(confirmAction.id, confirmAction.targetStatus);
      setConfirmAction(null);
    }
  };

  return (
    <>
      <Box
        style={{
          position: 'sticky',
          bottom: 0,
          background: 'var(--mantine-color-body)',
          borderTop: '1px solid var(--mantine-color-default-border)',
          padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-sm)',
          zIndex: 10,
        }}
      >
        <Group gap="xs" justify="flex-end">
          <Text size="xs" c="dimmed" mr="auto">Quick Actions</Text>
          {availableActions.map(action => {
            const Icon = action.icon;
            return (
              <Tooltip
                key={action.id}
                label={action.shortcut ? `${action.label} (${action.shortcut})` : action.label}
              >
                <Button
                  size="xs"
                  variant="light"
                  color={action.color}
                  leftSection={<Icon size={14} />}
                  onClick={() => handleAction(action)}
                >
                  {action.label}
                </Button>
              </Tooltip>
            );
          })}
          {extraActions}
        </Group>
      </Box>

      {/* Confirmation Modal */}
      <Modal
        opened={!!confirmAction}
        onClose={() => setConfirmAction(null)}
        title={`Confirm: ${confirmAction?.label}`}
        size="sm"
        zIndex={1200}
      >
        <Stack gap="md">
          <Text size="sm">{confirmAction?.confirmMessage}</Text>
          {confirmAction?.targetStatus && (
            <Text size="xs" c="dimmed">
              Status will change to: <strong>{confirmAction.targetStatus}</strong>
            </Text>
          )}
          <Divider />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="sm" onClick={() => setConfirmAction(null)}>
              Cancel
            </Button>
            <Button
              color={confirmAction?.color ?? 'blue'}
              size="sm"
              onClick={handleConfirm}
            >
              {confirmAction?.label}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
